import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';
import FileUpload from './FileUpload';
import { uploadQAFile, generateQAAnswers, updateQA, getQA } from '../api';

const STATUS_COLORS = {
  pending: { bg: '#FFF3CD', color: '#856404' },
  drafted: { bg: '#E8F4FD', color: '#0C5460' },
  reviewed: { bg: '#D4EDDA', color: '#155724' },
  approved: { bg: '#C3E6CB', color: '#155724' },
};

export default function BuyerQA({ deal, onUpdate }) {
  const [questions, setQuestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAnswer, setEditAnswer] = useState('');

  useEffect(() => {
    loadQA();
  }, [deal.id]);

  const loadQA = async () => {
    try {
      const res = await getQA(deal.id);
      setQuestions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      await uploadQAFile(deal.id, files[0]);
      await loadQA();
      onUpdate();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    setUploading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateQAAnswers(deal.id);
      setQuestions(res.data);
      onUpdate();
    } catch (err) {
      alert('Generation failed: ' + err.message);
    }
    setGenerating(false);
  };

  const handleSaveAnswer = async (qaId) => {
    try {
      await updateQA(qaId, { final_answer: editAnswer, status: 'reviewed' });
      setEditingId(null);
      await loadQA();
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  const handleApprove = async (qaId) => {
    try {
      await updateQA(qaId, { status: 'approved' });
      await loadQA();
    } catch (err) {
      alert('Approval failed: ' + err.message);
    }
  };

  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24, marginBottom: 20 };

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 20 }}>B-Buyer Q&A</h3>

      {/* Upload */}
      <div style={card}>
        <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Upload Investor Questions</h4>
        <FileUpload
          onFiles={handleUpload}
          accept=".xlsx,.xls,.csv"
          label={uploading ? 'Uploading...' : 'Drop Excel file with investor questions'}
          multiple={false}
        />
      </div>

      {/* Generate answers */}
      {questions.length > 0 && questions.some((q) => q.status === 'pending') && (
        <div style={{ marginBottom: 20 }}>
          <button onClick={handleGenerate} disabled={generating}
            style={{ padding: '12px 24px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: generating ? 0.7 : 1 }}>
            {generating ? 'Generating Answers...' : 'Generate Answers'}
          </button>
          <span style={{ marginLeft: 12, fontSize: 13, color: COLORS.textLight }}>
            {questions.filter((q) => q.status === 'pending').length} pending questions
          </span>
        </div>
      )}

      {/* Q&A list */}
      {questions.map((q) => {
        const sc = STATUS_COLORS[q.status] || STATUS_COLORS.pending;
        return (
          <div key={q.id} style={{ ...card, borderLeft: `3px solid ${sc.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.gold, marginRight: 8 }}>Q{q.question_number}</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: COLORS.navy }}>{q.question_text}</span>
              </div>
              <span style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, flexShrink: 0, marginLeft: 12,
                background: sc.bg, color: sc.color,
              }}>{q.status}</span>
            </div>

            {q.ai_draft_answer && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>DRAFT ANSWER</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.text, padding: 12, background: '#F8F9FA', borderRadius: 4 }}>
                  {q.ai_draft_answer}
                </div>
                {q.source_references && (
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4 }}>Sources: {q.source_references}</div>
                )}
              </div>
            )}

            {editingId === q.id ? (
              <div>
                <textarea
                  value={editAnswer}
                  onChange={(e) => setEditAnswer(e.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13, resize: 'vertical', marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleSaveAnswer(q.id)}
                    style={{ padding: '6px 14px', background: COLORS.success, color: COLORS.white, border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    Save
                  </button>
                  <button onClick={() => setEditingId(null)}
                    style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                {q.ai_draft_answer && q.status !== 'approved' && (
                  <button onClick={() => { setEditingId(q.id); setEditAnswer(q.final_answer || q.ai_draft_answer); }}
                    style={{ padding: '6px 14px', background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    Edit Answer
                  </button>
                )}
                {q.status === 'reviewed' && (
                  <button onClick={() => handleApprove(q.id)}
                    style={{ padding: '6px 14px', background: COLORS.success, color: COLORS.white, border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    Approve
                  </button>
                )}
              </div>
            )}

            {q.final_answer && q.final_answer !== q.ai_draft_answer && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.success, marginBottom: 4 }}>FINAL ANSWER</div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.text, padding: 12, background: '#F0FFF4', borderRadius: 4 }}>
                  {q.final_answer}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {questions.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: COLORS.textLight }}>
          Upload an Excel file with investor questions to get started.
        </div>
      )}
    </div>
  );
}
